<template>
    <v-form v-model="valid" data-cy="b2c-authorization-form" @keyup.native.enter="submit">
        <v-text-field
            v-model.trim="email"
            :label="email && showLabel(t('email'))"
            :placeholder="showPlaceholder(t('enter_your_email'))"
            :rules="validationRules('email', [emailRule, requiredRule])"
            type="email"
            data-cy="authorization-email-input"
            class="mb-3"
        />
        <v-text-field
            v-model.trim="password"
            :label="password && showLabel(t('password'))"
            :placeholder="showPlaceholder(t('enter_your_password'))"
            :rules="validationRules('password', [requiredRule])"
            type="password"
            data-cy="authorization-password-input"
        />

        <div class="mb-5 d-flex justify-end">
            <a
                href="#"
                class="text--primary text-decoration-none text-caption"
                data-cy="forgot_your_password-btn"
                @click.prevent="emit('click-forgot-password')"
            >
                {{ t('forgot_your_password') }}?
            </a>
        </div>

        <v-btn
            :loading="isLoading"
            :disabled="isLoading"
            variant="flat"
            color="primary"
            block
            data-cy="submit-button"
            class="mb-5"
            @click="submit"
        >
            {{ t('sign_in') }}
        </v-btn>
        <div v-if="hasAccessToRegistration" class="text-center">
            {{ `${t('dont_have_an_account')}?` }}
            <nuxt-link
                :to="{name: 'register'}"
                data-cy="registration-link"
                class="text-decoration-none"
                @click="emit('close-modal')"
            >
                {{ t('register') }}
            </nuxt-link>
        </div>
    </v-form>
</template>

<script setup lang="ts">
    import {transferFavoritesFromStorage} from '@/utils/favorite'
    import {EventBus, LOAD_WISHLIST} from '@/utils/event-bus'
    import {requiredRule, validationRules, emailRule} from '~/composables/rules'

    const {hasAccessToRegistration = true} = defineProps<{
        hasAccessToRegistration?: boolean
    }>()

    const emit = defineEmits<{
        (e: 'click-forgot-password'): void
        // eslint-disable-next-line @typescript-eslint/unified-signatures
        (e: 'close-modal'): void
        // eslint-disable-next-line @typescript-eslint/unified-signatures
        (e: 'auth-success'): void
    }>()

    const app = useNuxtApp()
    const $toast = app.$toast
    const {t} = useI18n()

    const isLoading = ref<boolean>(false)
    const valid = ref<boolean>(false)
    const email = ref<string>(null)
    const password = ref<string>(null)

    const {showLabel, showPlaceholder} = useShowLabel()

    const submit = async (): Promise<void> => {
        if (!valid.value) return
        isLoading.value = true

        try {
            await authStore.auth({
                login: email.value,
                password: password.value,
            })

            await transferFavoritesFromStorage(authStore.person.personId)
            EventBus.$emit(LOAD_WISHLIST)
            emit('auth-success')
            $toast.success(t('messages.success.authorized'), {timeout: 2000})
        } catch (e) {
            console.error(e)
            if (e.status === 401) {
                $toast.error(t('incorrect_credentials'))
            } else if (e.errors && e.errors.length) {
                $toast.error(e.errors[0].message)
            } else if (e.msg) app.$toast.error(e.msg)
        } finally {
            isLoading.value = false
        }
    }
</script>
